import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Plus, Bell, User, ChevronRight, FileText } from "lucide-react";
import { CATEGORY_CONFIG_MINIMAL, IconConstruction, IconShield, IconAssistant } from "../components/MinimalIcons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, requestsRes, contractsRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/stats`, { headers }),
          fetch(`${API_URL}/api/services/requests`, { headers }),
          fetch(`${API_URL}/api/contracts`, { headers })
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (requestsRes.ok) setRequests(await requestsRes.json());
        if (contractsRes.ok) setContracts(await contractsRes.json());
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  const firstName = user?.name?.split(" ")[0];
  const activeContracts = contracts.filter(c => ["active", "in_progress"].includes(c.status));

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="client-dashboard">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            eZelo.
          </Link>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate("/assistant")}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
              data-testid="assistant-btn"
            >
              <IconAssistant className="w-5 h-5 text-neutral-500" />
            </button>
            <button className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors">
              <Bell className="w-5 h-5 text-neutral-500" strokeWidth={1.5} />
            </button>
            <button 
              onClick={() => navigate("/profile")}
              className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center overflow-hidden"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-neutral-500" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="px-5 py-8 space-y-10">
        {/* Welcome */}
        <div className="animate-up">
          <p className="text-neutral-400 text-xs uppercase tracking-widest font-medium">Olá, {firstName}</p>
          <h1 className="text-2xl font-semibold text-neutral-900 mt-2 tracking-tight">
            O que você precisa hoje?
          </h1>
        </div>

        {/* Quick Actions - Minimal Grid */}
        <div className="grid grid-cols-2 gap-3 animate-up stagger-1">
          <button 
            onClick={() => navigate("/discovery")}
            className="p-5 bg-neutral-900 rounded-2xl text-left"
            data-testid="new-request-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
              <Plus className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <p className="font-medium text-white">Novo serviço</p>
            <p className="text-neutral-400 text-sm mt-0.5">Solicitar agora</p>
          </button>

          <button 
            onClick={() => navigate("/projects")}
            className="p-5 border border-neutral-100 rounded-2xl text-left hover:border-neutral-200 transition-all"
            data-testid="my-projects-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center mb-4">
              <IconConstruction className="w-5 h-5 text-neutral-700" />
            </div>
            <p className="font-medium text-neutral-900">Minhas Obras</p>
            <p className="text-neutral-500 text-sm mt-0.5">Construção</p>
          </button>
        </div>

        {/* Secondary Actions */}
        <div className="grid grid-cols-2 gap-3 animate-up stagger-2">
          <button 
            onClick={() => navigate("/direct-provider")}
            className="p-4 bg-neutral-50 rounded-2xl text-left flex items-center gap-3"
            data-testid="direct-provider-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <IconShield className="w-5 h-5 text-neutral-600" />
            </div>
            <div>
              <p className="font-medium text-neutral-900 text-sm">Meu profissional</p>
              <p className="text-neutral-500 text-xs">Pagamento protegido</p>
            </div>
          </button>

          <button 
            onClick={() => navigate("/contracts")}
            className="p-4 bg-neutral-50 rounded-2xl text-left flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <FileText className="w-5 h-5 text-neutral-600" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-medium text-neutral-900 text-sm">Contratos</p>
              <p className="text-neutral-500 text-xs">Ver todos</p>
            </div>
          </button>
        </div>

        {/* Stats - Minimal */}
        <div className="animate-up stagger-3">
          <div className="flex gap-8 overflow-x-auto pb-2 scrollbar-hide -mx-5 px-5">
            <div className="flex-shrink-0">
              <p className="text-3xl font-semibold text-neutral-900">{stats?.total_requests || 0}</p>
              <p className="text-neutral-400 text-xs uppercase tracking-widest mt-1">Solicitações</p>
            </div>
            <div className="w-px bg-neutral-100"></div>
            <div className="flex-shrink-0">
              <p className="text-3xl font-semibold text-amber-600">{stats?.pending_requests || 0}</p>
              <p className="text-neutral-400 text-xs uppercase tracking-widest mt-1">Pendentes</p>
            </div>
            <div className="w-px bg-neutral-100"></div>
            <div className="flex-shrink-0">
              <p className="text-3xl font-semibold text-emerald-600">{stats?.active_contracts || 0}</p>
              <p className="text-neutral-400 text-xs uppercase tracking-widest mt-1">Em andamento</p>
            </div>
          </div>
        </div>

        {/* Active Contracts */}
        {activeContracts.length > 0 && (
          <div className="animate-up stagger-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium text-neutral-900">Em andamento</h2>
              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full">
                {activeContracts.length}
              </span>
            </div>
            <div className="space-y-2">
              {activeContracts.slice(0, 2).map(contract => {
                const config = CATEGORY_CONFIG_MINIMAL[contract.category_id] || CATEGORY_CONFIG_MINIMAL.cat_diarista;
                const Icon = config.Icon;
                return (
                  <button
                    key={contract.contract_id}
                    onClick={() => navigate(`/execution/${contract.contract_id}`)}
                    className="w-full flex items-center gap-4 p-4 border border-neutral-100 rounded-2xl hover:border-neutral-200 transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl ${config.bg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${config.text}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-neutral-900">#{contract.contract_id.slice(-6)}</p>
                      <p className="text-neutral-500 text-sm">
                        R$ {contract.total_value?.toLocaleString("pt-BR")}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-neutral-300" strokeWidth={1.5} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Requests */}
        <div className="animate-up stagger-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-neutral-900">Solicitações recentes</h2>
            <button 
              onClick={() => navigate("/discovery")}
              className="text-xs text-neutral-400 hover:text-neutral-900 transition-colors"
            >
              Ver todas
            </button>
          </div>

          {requests.length === 0 ? (
            <div className="p-8 bg-neutral-50 rounded-2xl text-center">
              <div className="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-neutral-400" strokeWidth={1.5} />
              </div>
              <p className="font-medium text-neutral-900 mb-1">Nenhuma solicitação</p>
              <p className="text-neutral-500 text-sm mb-4">Crie sua primeira solicitação</p>
              <Button 
                onClick={() => navigate("/discovery")}
                className="btn-minimal btn-primary h-10 px-6 text-sm"
              >
                Nova solicitação
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {requests.slice(0, 3).map(request => {
                const config = CATEGORY_CONFIG_MINIMAL[request.category_id] || CATEGORY_CONFIG_MINIMAL.cat_diarista;
                const Icon = config.Icon;
                return (
                  <button 
                    key={request.request_id}
                    className="w-full flex items-center gap-4 p-4 border border-neutral-100 rounded-2xl hover:border-neutral-200 transition-all"
                    onClick={() => navigate(`/diagnostic/${request.category_id}`)}
                  >
                    <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${config.text}`} />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium text-neutral-900 truncate">{request.title}</p>
                      <p className="text-neutral-500 text-sm">{new Date(request.created_at).toLocaleDateString("pt-BR")}</p>
                    </div>
                    <span className="px-2 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-medium rounded-full">
                      {request.status === "pending" ? "Pendente" : request.status}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
